import { Fragment, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranscriptions, CallGroup } from '../../hooks/useTranscriptions';
import CallCard from './CallCard';
import api from '../../lib/axios';

interface TranscriptionSidebarProps {
  open: boolean;
  onClose: () => void;
}

type Tab = 'live' | 'history';

export default function TranscriptionSidebar({
  open,
  onClose,
}: TranscriptionSidebarProps) {
  const [activeTab, setActiveTab] = useState<Tab>('live');
  const { callGroups, connected } = useTranscriptions(open);

  return (
    <Transition show={open} as={Fragment}>
      <div className="fixed inset-y-0 right-0 z-40 flex">
        {/* Overlay for smaller screens */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/20 xl:hidden"
            onClick={onClose}
          />
        </Transition.Child>

        {/* Sidebar panel */}
        <Transition.Child
          as={Fragment}
          enter="transition-transform duration-300 ease-out"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition-transform duration-200 ease-in"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
        >
          <div className="relative flex w-80 flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-900">
                  Transcriptions
                </h2>
                {connected && (
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                )}
                {!connected && open && (
                  <span className="text-xs text-gray-400">Reconnecting...</span>
                )}
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-gray-100 p-1 text-gray-500 hover:bg-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-5">
              <button
                onClick={() => setActiveTab('live')}
                className={`mr-6 border-b-2 py-2.5 text-sm font-medium ${
                  activeTab === 'live'
                    ? 'border-teal-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Live
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`border-b-2 py-2.5 text-sm font-medium ${
                  activeTab === 'history'
                    ? 'border-teal-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                History
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {activeTab === 'live' ? (
                <LiveTab callGroups={callGroups} />
              ) : (
                <HistoryTab />
              )}
            </div>
          </div>
        </Transition.Child>
      </div>
    </Transition>
  );
}

function LiveTab({ callGroups }: { callGroups: CallGroup[] }) {
  if (callGroups.length === 0) {
    return (
      <div className="py-12 text-center">
        <svg
          className="mx-auto mb-3 h-10 w-10 text-gray-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>
        <p className="text-sm font-medium text-gray-500">No active calls</p>
        <p className="mt-1 text-xs text-gray-400">
          Calls will appear here in real-time
        </p>
      </div>
    );
  }

  const liveCount = callGroups.filter((g) => g.isLive).length;

  return (
    <div>
      {callGroups.map((group) => (
        <CallCard key={group.call_sid} group={group} />
      ))}
      <div className="py-4 text-center text-xs text-gray-400">
        {liveCount} active call{liveCount !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

function HistoryTab() {
  const [calls, setCalls] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchHistory = (pageNum: number, searchTerm: string) => {
    setLoading(true);
    const params: Record<string, string | number> = {
      page: pageNum,
      per_page: 25,
    };
    if (searchTerm) {
      params.search = searchTerm;
    }

    api
      .get('/api/transcripts', { params })
      .then((res) => {
        const newCalls = res.data.data || [];
        if (pageNum === 1) {
          setCalls(newCalls);
        } else {
          setCalls((prev) => [...prev, ...newCalls]);
        }
        const meta = res.data._metadata;
        setHasMore(meta?.page < meta?.pages);
      })
      .catch(() => {
        // Show empty on error
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchHistory(1, search);
  }, [search]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchHistory(nextPage, search);
  };

  return (
    <div>
      {/* Search */}
      <div className="mb-3">
        <input
          type="text"
          placeholder="Search by caller name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder-gray-400 focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-400"
        />
      </div>

      {loading && calls.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
      ) : calls.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-400">
          No call history found
        </div>
      ) : (
        <>
          {calls.map((call) => (
            <HistoryCallCard key={call.id} call={call} />
          ))}
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="mb-4 w-full rounded-md border border-gray-200 py-2 text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function HistoryCallCard({ call }: { call: any }) {
  const initials = (call.caller_name || 'UN')
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const transcripts = call.transcripts || [];

  return (
    <div className="mb-3 overflow-hidden rounded-lg border border-gray-200">
      <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
            {initials}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-gray-900">
              {call.caller_name || 'Unknown Caller'}
            </div>
            <div className="text-xs text-gray-400">
              {new Date(call.created_at).toLocaleDateString()} &middot;{' '}
              {transcripts.length} segments
            </div>
          </div>
        </div>
      </div>
      {transcripts.length > 0 && (
        <div className="bg-gray-50 px-4 py-2 max-h-40 overflow-y-auto">
          {transcripts.map((t: any) => (
            <div key={t.id} className="mb-1.5 text-xs text-gray-600">
              <span
                className={`mr-1 font-bold uppercase ${
                  t.track === 'inbound_track'
                    ? 'text-blue-500'
                    : 'text-purple-500'
                }`}
              >
                {t.track === 'inbound_track' ? 'Caller' : 'Agent'}:
              </span>
              {t.transcript}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
